import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import LogLayout from "../components/LogLayout";
import SectionTabs, { TOURS_TABS } from "../components/SectionTabs";
import { FileText, Plus, Download, Trash2, X, Calendar, Building2, Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";

export default function LogBordereauxPage() {
  const [bordereaux, setBordereaux] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [mode, setMode] = useState("insurer");
  const [selectedInsurer, setSelectedInsurer] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [title, setTitle] = useState("Bordereau de dépôt");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const h = getHeaders();
      const [brdRes, invRes, insRes] = await Promise.all([
        axios.get(`${API}/log/bordereaux`, { headers: h }),
        axios.get(`${API}/log/invoices`, { headers: h }),
        axios.get(`${API}/log/insurers`, { headers: h })
      ]);
      setBordereaux(brdRes.data.bordereaux || []);
      setInvoices(invRes.data.invoices || []);
      setInsurers(insRes.data.insurers || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = { title };
      if (mode === "insurer") {
        if (!selectedInsurer) { toast.error("Selectionnez un assureur"); setGenerating(false); return; }
        body.insurer_id = selectedInsurer;
      } else {
        if (selectedInvoices.length === 0) { toast.error("Selectionnez des factures"); setGenerating(false); return; }
        body.invoice_ids = selectedInvoices;
      }

      const res = await axios.post(`${API}/log/bordereaux/generate`, body, {
        headers: { ...getHeaders(), "Accept": "application/pdf" },
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bordereau_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Bordereau genere et téléchargé");
      setShowGenerate(false);
      setSelectedInsurer("");
      setSelectedInvoices([]);
      load();
    } catch (e) {
      const errText = e.response?.data ? await e.response.data.text?.() : "";
      toast.error(errText || "Erreur generation");
    }
    finally { setGenerating(false); }
  };

  const handleDownload = async (id, docNumber) => {
    try {
      const res = await axios.get(`${API}/log/bordereaux/${id}/download`, {
        headers: getHeaders(), responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur téléchargément"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce bordereau ?")) return;
    try {
      await axios.delete(`${API}/log/bordereaux/${id}`, { headers: getHeaders() });
      toast.success("Bordereau supprimé");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const filteredInvoices = mode === "insurer" && selectedInsurer
    ? invoices.filter(i => i.insurer_id === selectedInsurer)
    : invoices;

  return (
    <LogLayout title="Tournées" subtitle="Bordereaux : génération et historique des bordereaux de dépôt">
      <SectionTabs tabs={TOURS_TABS} />
      <div data-testid="log-bordereaux-page" className="space-y-4">
        <div className="flex justify-end">
          <Button data-testid="generate-bordereau-btn" onClick={() => setShowGenerate(true)} className="bg-blue-600 hover:bg-blue-500/100 text-white gap-2">
            <Plus className="w-4 h-4" /> Générér un bordereau
          </Button>
        </div>

        {/* History */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : bordereaux.length === 0 ? (
          <div className="text-center py-16 bg-slate-900 rounded-xl border border-white/5">
            <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucun bordereau genere</p>
            <p className="text-xs text-slate-500 mt-1">Généréz votre premier bordereau pour le retrouver ici</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bordereaux.map(brd => (
              <div key={brd.id} data-testid={`bordereau-row-${brd.id}`} className="bg-slate-900 border border-white/5 rounded-xl p-4 hover:border-blue-500/20 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/100/15 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{brd.doc_number}</p>
                      <p className="text-[10px] text-slate-500">{brd.title} - {brd.insurer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-bold text-white">{formatFCFA(brd.total_amount)}</p>
                      <p className="text-[10px] text-slate-500">{brd.invoice_count} facture(s) - {new Date(brd.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <button onClick={() => handleDownload(brd.id, brd.doc_number)} className="p-2 rounded-lg hover:bg-blue-500/100/10 text-blue-400" data-testid={`download-bordereau-${brd.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(brd.id)} className="p-2 rounded-lg hover:bg-red-500/100/100/10 text-slate-500 hover:text-red-400" data-testid={`delete-bordereau-${brd.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Generate Modal */}
        {showGenerate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="generate-bordereau-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b border-white/5">
                <h2 className="text-lg font-bold text-white">Générér un bordereau</h2>
                <button onClick={() => setShowGenerate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Titre du bordereau</label>
                  <input data-testid="bordereau-title" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none" />
                </div>

                {/* Mode selector */}
                <div className="flex gap-2">
                  <button onClick={() => setMode("insurer")} data-testid="mode-insurer" className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === "insurer" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
                    <Building2 className="w-4 h-4 inline mr-2" /> Par assureur
                  </button>
                  <button onClick={() => setMode("manual")} data-testid="mode-manual" className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === "manual" ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
                    <Filter className="w-4 h-4 inline mr-2" /> Selection manuelle
                  </button>
                </div>

                {mode === "insurer" && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Assureur</label>
                    <select data-testid="bordereau-insurer-select" value={selectedInsurer} onChange={e => setSelectedInsurer(e.target.value)} className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:outline-none">
                      <option value="">Selectionner un assureur</option>
                      {insurers.map(ins => <option key={ins.id} value={ins.id}>{ins.name}</option>)}
                    </select>
                    {selectedInsurer && <p className="text-[10px] text-slate-500 mt-1">{filteredInvoices.length} facture(s) pour cet assureur</p>}
                  </div>
                )}

                {mode === "manual" && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-2">Selectionner les factures</label>
                    <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-950 border border-white/10 rounded-lg p-2">
                      {invoices.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-3">Aucune facture</p>
                      ) : invoices.map(inv => (
                        <label key={inv.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm">
                          <input type="checkbox" checked={selectedInvoices.includes(inv.id)} onChange={() => toggleInvoice(inv.id)} className="rounded border-white/20 bg-slate-900" />
                          <span className="text-white">{inv.invoice_number}</span>
                          <span className="text-slate-500 text-xs ml-auto">{inv.insurer_name} - {formatFCFA(inv.amount)}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">{selectedInvoices.length} facture(s) selectionnee(s)</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowGenerate(false)} className="flex-1 border-white/10 text-slate-300">Annuler</Button>
                  <Button onClick={handleGenerate} disabled={generating} data-testid="submit-bordereau-btn" className="flex-1 bg-blue-600 hover:bg-blue-500/100 text-white">
                    {generating ? "Generation..." : "Générér PDF"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}
